import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';

const groupByOptions = [
  { id: 'brand', label: 'Brand' },
  { id: 'Manufacturer', label: 'Manufacturer' }
];

/**
 *
 * Sales Summary double dropdown
 */
const AtlasSalesNon1PMarketShareColumnChart = () => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState('brand');
  const currentGroupByOption =
    groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];

  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2DorisColumnData({
    metricType: 'retailSales',
    fieldName: selectedGroupByFieldId,
    queryKey: 'Atlas-retailSales-target',
    endPoint: 'sales/GetSalesMetrics'
  });

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2DorisColumnData({
    metricType: 'retailSales',
    fieldName: 'retailer',
    queryKey: 'Atlas-retailSales-market',
    endPoint: 'sales/GetSalesMetrics'
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isMarketShareLoading && !isTargetLoading) {
      const currentMarketShare = marketShareData?.[0]?.values?.retailer;
      const compareMarketShare = marketShareData?.[0]?.comparisonValues?.retailer;

      targetData.forEach((datum) => {
        const datumName = datum?.metadata?.name;
        const datumId = datum?.fieldId;

        const formattedCategoryObj = {
          name: datumName,
          id: datumId,
          type: currentGroupByOption?.id
        };

        const currentDatum = datum?.values?.[currentGroupByOption?.id] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[currentGroupByOption?.id] ?? 0;

        comparisonData.push([datumName, safeDivide(comparisonDatum, compareMarketShare)]);
        currentData.push([datumName, safeDivide(currentDatum, currentMarketShare)]);
        categoryData.push(formattedCategoryObj);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [currentGroupByOption, isMarketShareLoading, isTargetLoading, marketShareData, targetData]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Category Market Share by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Retail Sales - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isMarketShareLoading || isTargetLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center" gap={4}>
          <Text variant="h4">Market Share of Retail Sales</Text>
          <Text variant="h4">by</Text>
          <SlDropdownMenu
            buttonVariant="title"
            options={groupByOptions}
            selectedId={selectedGroupByFieldId}
            onChange={(option) => setSelectedGroupByFieldId(option.id)}
            defaultLabel="Brand"
          />
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasSalesNon1PMarketShareColumnChart;
